import { TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * The timesheet over-budget signal (docs/budgets.md § Signals): a red badge
 * — overage first — plus, on the specific entries that pushed a customer
 * budget's pool over its allowance, a red-filled hours cell with a corner
 * wedge. Purely informational; nothing here ever disables an action.
 *
 * The fill/wedge is deliberately independent of approval-status styling
 * (left stripe) — see `OverBudgetCell` — so the two compose instead of
 * colliding when a surface eventually shows both on the same row.
 */

/** "9.5" / "12" — trims trailing zeros, no unit suffix (callers append "h"
 *  or "h over" themselves so the tooltip's "45/40h" pairing reads as one
 *  unit, not "45h/40h"). */
function hoursShort(n: number): string {
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/\.?0+$/, '');
}

export interface OverBudgetDetail {
  roleName: string | null;
  periodLabel: string;
  budgetHours: number;
  consumedHours: number;
}

interface OverBudgetBadgeProps {
  /** Total overage this badge summarizes. */
  overHours: number;
  /** Distinct budgets over their allowance. Omit for an entry-level badge
   *  (one entry can only ever count against one budget/role/period). */
  budgetCount?: number;
  /** Per-budget breakdown shown in a hover tooltip, e.g.
   *  "Controller: 45/40h — Jul 2026". Omit when there's nothing to add. */
  details?: OverBudgetDetail[];
  className?: string;
}

/** "▲ 5h over" / "▲ 11.5h over · 2 budgets" — red, matches the palette
 *  `ApprovalStatusBadge` uses for `rejected` so the two read as one
 *  language on the approvals screen. */
export function OverBudgetBadge({
  overHours,
  budgetCount,
  details,
  className,
}: OverBudgetBadgeProps) {
  const badge = (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded border px-1.5 py-0.5 text-[11px] font-medium',
        'border-red-200 bg-red-50 text-red-700 dark:border-red-800/60 dark:bg-red-900/30 dark:text-red-200',
        className,
      )}
    >
      <TriangleAlert className="size-3 shrink-0" aria-hidden />
      {hoursShort(overHours)}h over
      {budgetCount != null ? ` · ${budgetCount} budget${budgetCount === 1 ? '' : 's'}` : ''}
    </span>
  );
  if (!details || details.length === 0) return badge;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-0.5 text-xs">
            {details.map((d) => (
              <div key={`${d.roleName ?? 'unknown'}:${d.periodLabel}`}>
                {d.roleName ?? 'Unknown role'}: {hoursShort(d.consumedHours)}/
                {hoursShort(d.budgetHours)}h — {d.periodLabel}
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Wraps an hours-cell's contents with the over-budget fill + corner wedge.
 * FILL ONLY — no left border — so approval status (which owns the left
 * stripe, per docs/budgets.md) can compose with this rather than collide.
 */
export function OverBudgetCell({
  overHours,
  className,
  children,
}: {
  overHours: number;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-md bg-red-100 px-1 py-0.5 dark:bg-red-900/40',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute right-0 top-0 h-0 w-0',
          'border-[7px] border-b-transparent border-l-transparent border-r-red-500 border-t-red-500',
          'dark:border-r-red-400 dark:border-t-red-400',
        )}
      />
      {children}
      <div className="text-[10px] font-semibold leading-tight text-red-700 dark:text-red-300">
        {hoursShort(overHours)}h over
      </div>
    </div>
  );
}
